/**
 * InspectorMode — Time-bounded, deterministic, read-only.
 *
 * Source: drafts/31_ux_design_system_spec.md §4.5 Inspector Session Pattern.
 *
 * CRITICAL — AI surface MUST be absent per AI-009.
 * Banner persists across all views. Deterministic query builder ONLY.
 *
 * Data: audit events queried server-side via /audit/events with the filter
 * params. Filters are deliberately a plain object (no AI suggestions, no
 * fuzzy match) per AI-009.
 */

import { useEffect, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, Input, Table, type ColumnConfig } from '@edms/design-system';
import styles from './InspectorMode.module.css';
import { useAuditEvents, type AuditEvent, type AuditEventFilters } from '../../api/hooks';

const columns: ColumnConfig<AuditEvent>[] = [
  { key: 'event_at_utc', header: 'UTC Timestamp', cell: (r) => r.event_at_utc, sortBy: (r) => r.event_at_utc, width: '200px' },
  { key: 'event_code', header: 'Event', cell: (r) => r.event_code, sortBy: (r) => r.event_code, width: '180px' },
  { key: 'entity_type', header: 'Entity', cell: (r) => `${r.entity_type} ${r.entity_id}`, width: '220px' },
  { key: 'actor_name', header: 'Actor', cell: (r) => r.actor_name, sortBy: (r) => r.actor_name, width: '160px' },
  { key: 'ip_address', header: 'IP', cell: (r) => r.ip_address, width: '120px' },
];

interface FilterForm {
  fromDate: string;
  toDate: string;
  eventCode: string;
  actor: string;
}

const EMPTY_FILTERS: FilterForm = { fromDate: '', toDate: '', eventCode: '', actor: '' };

function toApiFilters(form: FilterForm): AuditEventFilters {
  // datetime-local values come in as `YYYY-MM-DDTHH:MM`. Append `:00Z` for the API
  // so the server sees an explicit UTC timestamp (the audit_log column is timestamptz).
  const fmt = (v: string) => (v ? `${v}:00Z` : undefined);
  return {
    from: fmt(form.fromDate),
    to: fmt(form.toDate),
    event_code: form.eventCode.trim() || undefined,
    actor: form.actor.trim() || undefined,
  };
}

interface CurrentScenarioState {
  scenario: string;
  loadedAt: string | null;
  totalRows: number;
  mockMode?: boolean;
}

export function InspectorMode() {
  const { t } = useTranslation();
  const [sessionRemaining, setSessionRemaining] = useState(3 * 60 * 60 + 47 * 60); // 3h 47m mock
  const [formState, setFormState] = useState<FilterForm>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AuditEventFilters>({});
  const eventsQ = useAuditEvents(appliedFilters);

  // DDM-012 — demo-data-active disclosure. If the tenant's active scenario is
  // anything other than 'baseline-current', show a banner so the inspector
  // cannot mistake demo data for production data.
  const [demoBanner, setDemoBanner] = useState<CurrentScenarioState | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/api/v1/admin/data-scenarios/current'), { headers: buildHeaders() })
      .then((r) => r.json())
      .then((data: CurrentScenarioState) => {
        if (!cancelled && data?.scenario && data.scenario !== 'baseline-current' && data.scenario !== 'unknown') {
          setDemoBanner(data);
        }
      })
      .catch(() => { /* tolerate — endpoint missing on legacy deploys */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setSessionRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  const hours = Math.floor(sessionRemaining / 3600);
  const minutes = Math.floor((sessionRemaining % 3600) / 60);

  const events = eventsQ.data?.items ?? [];
  const totalLabel = eventsQ.isLoading ? '…' : String(eventsQ.data?.total ?? events.length);

  return (
    <div className={styles.inspector}>
      {/* Persistent banner per UX spec §4.5 — visible across ALL inspector views */}
      <div className={styles.banner} role="region" aria-label="Inspector session status">
        <div>
          <strong>{t('inspector.activeBanner', { defaultValue: 'INSPECTION MODE ACTIVE' })}</strong>
          <span className={styles.bannerDetail}>
            {t('inspector.timerFormat', {
              defaultValue: 'Session expires in {{h}}h {{m}}m — All activity audited',
              h: hours,
              m: minutes,
            })}
          </span>
        </div>
      </div>

      {/* DDM-012 — Demo-data-active disclosure. Renders below the session banner. */}
      {demoBanner && (
        <div
          className={styles.demoBanner}
          role="alert"
          aria-label="demo data active"
          data-scenario={demoBanner.scenario}
        >
          <strong>⚠ DEMO DATA ACTIVE</strong>
          <span className={styles.demoBannerDetail}>
            Active scenario: <code>{demoBanner.scenario}</code>
            {demoBanner.loadedAt && (
              <> · loaded {new Date(demoBanner.loadedAt).toLocaleString()}</>
            )}
            {' '}— records tagged <code>data_origin='demo'</code> or <code>'baseline'</code> are not production data.
          </span>
        </div>
      )}

      <header className={styles.header}>
        <h1 className={styles.title}>
          {t('inspector.studioTitle', { defaultValue: 'Audit Trail — Inspector Mode' })}
        </h1>
        <p className={styles.subtitle}>
          {t('inspector.subtitle', {
            defaultValue:
              'Deterministic query builder. AI assistance is NOT available in inspector mode (per AI-009).',
          })}
        </p>
      </header>

      {/* Query builder — deterministic ONLY */}
      <Card>
        <CardHeader>
          <h2 className={styles.sectionTitle}>
            {t('inspector.queryHeading', { defaultValue: 'Build Query' })}
          </h2>
        </CardHeader>
        <CardBody>
          <div className={styles.queryGrid}>
            <Input
              label={t('inspector.fromDate', { defaultValue: 'From (UTC)' })}
              type="datetime-local"
              value={formState.fromDate}
              onChange={(e) => setFormState((p) => ({ ...p, fromDate: e.target.value }))}
            />
            <Input
              label={t('inspector.toDate', { defaultValue: 'To (UTC)' })}
              type="datetime-local"
              value={formState.toDate}
              onChange={(e) => setFormState((p) => ({ ...p, toDate: e.target.value }))}
            />
            <Input
              label={t('inspector.eventCode', { defaultValue: 'Event code' })}
              placeholder="e.g., SIGNATURE_APPLIED"
              value={formState.eventCode}
              onChange={(e) => setFormState((p) => ({ ...p, eventCode: e.target.value }))}
            />
            <Input
              label={t('inspector.actor', { defaultValue: 'Actor' })}
              placeholder="username or full name"
              value={formState.actor}
              onChange={(e) => setFormState((p) => ({ ...p, actor: e.target.value }))}
            />
          </div>
          <div className={styles.queryActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setFormState(EMPTY_FILTERS);
                setAppliedFilters({});
              }}
            >
              {t('inspector.clearFilters', { defaultValue: 'Clear filters' })}
            </Button>
            <Button variant="primary" onClick={() => setAppliedFilters(toApiFilters(formState))}>
              {t('inspector.runQuery', { defaultValue: 'Run query' })}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <h2 className={styles.sectionTitle}>
            {t('inspector.resultsHeading', { defaultValue: 'Results' })} ({totalLabel})
          </h2>
          <Button variant="secondary" size="sm">
            {t('inspector.exportPack', { defaultValue: 'Export audit pack (PDF)' })}
          </Button>
        </CardHeader>
        <CardBody>
          {eventsQ.isError ? (
            <p role="alert">Failed to load audit events: {eventsQ.error?.message}</p>
          ) : (
            <Table
              rows={events}
              columns={columns}
              getRowId={(e) => e.audit_id}
              isLoading={eventsQ.isLoading}
              emptyState="No audit events match the current query."
              caption="Audit events matching the current query"
            />
          )}
        </CardBody>
      </Card>

      <p className={styles.footerNote}>
        {t('inspector.footerNote', {
          defaultValue:
            'This session is watermarked. Every query, view, and export is itself audited. AI features are explicitly disabled in inspector mode (AI-009 / drafts/03_prd §6.5.2).',
        })}
      </p>
    </div>
  );
}
